import { EnvironmentProviders, ErrorHandler, inject, provideAppInitializer, Provider } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';

// Google's iOS in-app browser injects inline scripts whose errors are not HMS Home code
export function isInjectedInlineScriptError(event: Sentry.ErrorEvent): boolean {
  const frameLists = (event.exception?.values ?? []).map(exception => exception.stacktrace?.frames ?? []);
  if (frameLists.length === 0 || frameLists.some(frames => frames.length === 0)) {
    return false;
  }

  return frameLists.every(frames => frames.every(frame => isInlinePageUrl(frame.abs_path ?? frame.filename)));
}

function isInlinePageUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    return false;
  }

  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
    return false;
  }

  const path = parsedUrl.pathname.toLowerCase();
  return !path.endsWith('.js') && !path.endsWith('.mjs');
}

export function initializeSentry(): void {
  if (!environment.sentry) {
    return;
  }

  Sentry.init({
    environment: environment.sentry.environment,
    release: `hms-home@${environment.version}`,
    dsn: environment.sentry.dsn,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration()
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: environment.sentry.tracesSampleRate,
    tracePropagationTargets: environment.sentry.tracePropagationTargets,
    beforeSend: event => (isInjectedInlineScriptError(event) ? null : event)
  });
}

export function provideSentry(): Array<Provider | EnvironmentProviders> {
  if (!environment.sentry) {
    return [];
  }

  return [
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    })
  ];
}
